import useAppStore from "@/context/store";
import {
  deleteConversation,
  getConversationList,
  getDmUnread,
  getMessageList,
  getSystemDMList,
  getUser,
  sendMessage,
  setMessageReaded,
  setSystemDMReaded,
  toBlackList,
} from "@/services";
import onlyVisibleRun from "@/utils/onlyVisibleRun";
import { useGetState, useMemoizedFn, useMount, useUpdateEffect } from "ahooks";
import { useCallback, useEffect, useRef, useState } from "react";
import HBMessage from "../HBMessage";
import HBConfirm from "../HBModal/confirm";

const MAX_PER_PAGE = 20;

export const CONVERSATION_TYPE = {
  CONTACT: "contact",
  SYSTEM: "system",
};

const makeNewConversation = (user) => ({
  _id_: "new",
  with_user: user,
  with_user_id: user.user_id,
  last_message: { text: "" },
  updated_at: Math.floor(Date.now() / 1000),
  _type_: CONVERSATION_TYPE.CONTACT,
});

export default function useViewModel({ targetUserId, targetUserUrlname }) {
  const [blocked, setBlocked] = useState(false);
  const [content, setContent] = useState();
  const user = useAppStore((store) => store.user);
  const [conversations, setConversations, getConversations] = useGetState([]);
  const [current, _setCurrent] = useState();

  const currentRef = useRef();
  const [, setHasMore, getHasMore] = useGetState(true);
  const msgList = useRef();
  const ref = useRef({ send: null, pollTaskId: null });

  const setCurrent = useCallback(
    (val) => {
      if (val) {
        Reflect.deleteProperty(val, "unread");
      }
      currentRef.current = val;
      _setCurrent(val ? { ...val } : null);
    },
    [currentRef, _setCurrent]
  );

  const sortConversations = useCallback(
    (conversations) => {
      conversations = conversations.sort((a, b) => {
        const ua = a.updated_at ?? a.created_at;
        const ub = b.updated_at ?? b.created_at;
        if (ua > ub) return -1;
        else if (ua < ub) return 1;
        return 0;
      });

      // 不排序
      const c = currentRef.current ?? conversations[0];
      setCurrent(c);

      setConversations([...conversations]);
    },
    [setCurrent, setConversations]
  );

  const loadConversations = useCallback(
    async ({ targetUserId, targetUserUrlname } = {}) => {
      const { err, conversations: _conversations } = await getConversationList({
        limit: 100,
      });
      if (err) return;

      let conversations = getConversations();
      for (const c of _conversations) {
        const same = conversations.find((r) => {
          if (r._type_ === CONVERSATION_TYPE.CONTACT) {
            return r.with_user_id === c.with_user_id;
          } else {
            return !c.conversation_id;
          }
        });
        if (!same) {
          conversations.push({
            ...c,
            _type_: c.conversation_id
              ? CONVERSATION_TYPE.CONTACT
              : CONVERSATION_TYPE.SYSTEM,
            _id_: c.conversation_id ?? c.system_dm_id,
          });
        }
      }
      const t = [...conversations];

      if (targetUserUrlname || targetUserId) {
        let currentConversation = t.find((c) => {
          if (targetUserId) {
            if (targetUserId === CONVERSATION_TYPE.SYSTEM) {
              return c._type_ === CONVERSATION_TYPE.SYSTEM;
            }
            return c.with_user?.user_id === targetUserId;
          }
          if (targetUserUrlname) {
            return c.with_user?.urlname === targetUserUrlname;
          }
        });
        if (!currentConversation) {
          const { err, user } = await getUser({
            user_id: targetUserId,
            urlname: targetUserUrlname,
          });
          if (err) return;
          currentConversation = makeNewConversation(user);
          t.push(currentConversation);
        }
        setCurrent(currentConversation);
      }

      sortConversations(t);
    },
    [setCurrent, sortConversations, getConversations]
  );
  const onSendClick = useCallback(async () => {
    const { push } = msgList.current;
    const { err, msg, captchaKey, ...msgData } = await sendMessage({
      to_user_id: current.with_user_id,
      text: content,
    });
    if (err && !captchaKey) {
      HBMessage.error(msg);
      return;
    }
    msgList.current.feedDog();
    push("contact", {
      ...msgData,
      type: "sent",
      to_user: current.with_user,
      from_user: user,
      _id_: msgData.direct_message_id,
    });
    setContent("");
    current.last_message.text = content;
    conversations.find((c) => c._id_ === current._id_).updated_at = Math.floor(
      Date.now() / 1000
    );
    sortConversations(conversations);
  }, [content, current, user, conversations, sortConversations]);

  const onContentChange = useCallback((e) => {
    setContent(e.target.value);
  }, []);

  const onBlockClick = useCallback(() => {
    HBConfirm.open({
      title: "加入黑名单",
      content: `加入黑名单，对方无法再关注你，并且无法给你发私信。可在「个人设置」中管理黑名单`,
      okText: "确定",
      onOk: async () => {
        const { err } = await toBlackList({
          item_type: 3,
          item_value: current.with_user.user_id,
        });
        if (err) {
          return;
        }
        const t = conversations.filter((r) => r._id_ !== current._id_);
        const ct = t[0] ?? null;
        setConversations(t);
        setCurrent(ct);
        if (!ct) msgList.current.clear();
        HBMessage.success("操作成功");
      },
    });
  }, [current, conversations, setCurrent, setConversations]);

  const onDeleteConversation = useCallback(
    async (with_user_id) => {
      const { err } = await deleteConversation({
        with_user_id,
      });
      if (err) {
        return;
      }

      const t = conversations.filter((r) => r.with_user_id !== with_user_id);
      if (current.with_user_id === with_user_id) {
        setCurrent(t[0] ?? null);
      }
      setConversations(t);
    },
    [current, conversations, setCurrent, setConversations]
  );

  const onConversationClick = (target) => async () => {
    setCurrent(target);
    setContent("");
  };

  const updateFn = useCallback(async () => {
    if (!current) return;

    const { clear } = msgList.current;
    if (current._type_ === CONVERSATION_TYPE.SYSTEM) {
      setHasMore(true);
      const { err, messages } = await getSystemDMList();
      if (err) return;
      clear(
        "system",
        messages
          .sort((a, b) => b.created_at - a.created_at)
          .map((m) => ({
            ...m,
            _id_: m.system_dm_id,
          }))
      );
    } else {
      const { err, messages, blocked } = await getMessageList({
        with_user_id: current.with_user_id,
        limit: MAX_PER_PAGE,
      });
      if (err) return;
      setBlocked(blocked);
      setHasMore(messages.length === MAX_PER_PAGE);
      clear(
        "contact",
        messages.map((m) => ({
          ...m,
          _id_: m.direct_message_id,
        }))
      );
    }
  }, [current, setHasMore]);

  useUpdateEffect(() => {
    updateFn();
  }, [updateFn]);

  const loadOldMessage = useMemoizedFn(async (reachTop) => {
    const { current } = currentRef;
    const { append, getOldestMessage } = msgList.current;
    if (!getHasMore() || !reachTop) return;
    if (current._type_ === CONVERSATION_TYPE.SYSTEM) return;

    const oldMsg = getOldestMessage();
    const { err, messages, blocked } = await getMessageList({
      with_user_id: current.with_user_id,
      limit: MAX_PER_PAGE,
      max: oldMsg?.message.direct_message_id,
    });
    if (err) return;
    setBlocked(blocked);
    setHasMore(messages.length === MAX_PER_PAGE);
    append(
      "contact",
      messages.map((m) => ({
        ...m,
        _id_: m.direct_message_id,
      }))
    );
  });

  const loadNewMessage = useMemoizedFn(async () => {
    const { current } = currentRef;
    const { clear, push, getNewestMessage, getPureMessages } = msgList.current;
    const newMsg = getNewestMessage();

    if (current._type_ === CONVERSATION_TYPE.SYSTEM) {
      const { err, messages } = await getSystemDMList();
      if (err) return;
      setBlocked(false);
      setHasMore(true);
      if (messages.length === getPureMessages().length) return;
      clear(
        "system",
        messages
          .sort((a, b) => b.created_at - a.created_at)
          .map((m) => ({
            ...m,
            _id_: m.system_dm_id,
          }))
      );
    } else {
      const { err, messages, blocked } = await getMessageList({
        with_user_id: current.with_user_id,
        limit: MAX_PER_PAGE,
        since: newMsg?.message.direct_message_id,
      });
      if (err) return;
      setBlocked(blocked);
      if (!messages.length) return;
      push(
        "contact",
        messages.map((m) => ({
          ...m,
          _id_: m.direct_message_id,
        }))
      );
      return messages;
    }
  });

  const onReachBottom = useMemoizedFn(async (visible) => {
    if (visible) {
      current.unread = false;
      currentRef.current.unread = false;
    }
  });

  useMount(() => {
    loadConversations({ targetUserUrlname, targetUserId });
  });

  useEffect(() => {
    ref.current.send = onSendClick;
  }, [onSendClick]);

  const markItReaded = useMemoizedFn(async (target) => {
    if (target._type_ === CONVERSATION_TYPE.SYSTEM) {
      const { err } = await setSystemDMReaded();
      if (err) return;
    } else {
      const { err } = await setMessageReaded({
        with_user_id: target.with_user_id,
      });
      if (err) return;
    }
    const unreadAmount = await getDmUnread();
    useAppStore.setState({ dmUnread: unreadAmount });
  });
  useEffect(() => {
    if (!current) {
      clearInterval(ref.current.pollTaskId);
      ref.current.pollTaskId = null;
      return;
    }

    markItReaded(current).catch((err) =>
      console.error("market it readed error:", err)
    );

    // 15s 轮训
    ref.current.pollTaskId = setInterval(
      onlyVisibleRun(async () => {
        const [messages] = await Promise.all([
          loadNewMessage(),
          loadConversations(),
        ]);
        if (messages?.length) {
          msgList.current.feedDog();
        }
      }),
      15 * 1000
    );

    const r = ref.current;
    return () => {
      clearInterval(r.pollTaskId);
    };
  }, [current, markItReaded, loadNewMessage, loadConversations]);

  return {
    blocked,
    content,
    user,
    conversations,
    current,
    msgList,

    onSendClick,
    onContentChange,
    onBlockClick,
    onDeleteConversation,
    onConversationClick,
    loadOldMessage,
    onReachBottom,
  };
}
